import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Camera, Maximize2, X, Sparkles, Pause, Play } from 'lucide-react';

interface PhotoItem {
  url: string;
  title: string;
  category: string;
}

const photoGallery: PhotoItem[] = [
  {
    url: '/assets/IMG_8852.JPG',
    title: 'Lorenz Saldivar',
    category: 'BSIT Graduate & Web Developer'
  },
  {
    url: '/assets/IMG_7316 1.jpg',
    title: 'Summit Views & Mountain Trails',
    category: 'Hiking & Adventure • Libona, Bukidnon'
  },
  {
    url: '/assets/IMG_2416 1.jpg',
    title: 'Trail Hiking & Endurance Training',
    category: 'Outdoor Fitness'
  },
  {
    url: '/assets/IMG_2300 1.jpg',
    title: 'High Altitude Mountain Peak',
    category: 'Hiking & Nature'
  },
  {
    url: '/assets/IMG_2622 1.jpg',
    title: 'Nature Trails & Scenic Heights',
    category: 'Adventure Exploration'
  },
  {
    url: '/assets/IMG_2933 1.jpg',
    title: 'Gym & Fitness Discipline',
    category: 'Personal Training & Discipline'
  },
  {
    url: '/assets/IMG_2956 1.jpg',
    title: 'Strength & Peak Performance',
    category: 'Fitness & Health'
  },
  {
    url: '/assets/IMG_3731.jpg',
    title: 'Trail Exploration & Fresh Air',
    category: 'Bukidnon Outdoors'
  },
  {
    url: '/assets/IMG_1948 1.jpg',
    title: 'Life & Journey Beyond the Screen',
    category: 'Lifestyle & Personal Growth'
  }
];

export const AboutPhotoCarousel: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [isSliding, setIsSliding] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<PhotoItem | null>(null);

  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  // Autoplay timer: advances every 2.5 seconds when NOT hovered and NOT in lightbox
  useEffect(() => {
    if (!isAutoPlaying || isHovered || lightboxImage) return;

    const timer = setInterval(() => {
      triggerSlide((prev) => (prev + 1) % photoGallery.length);
    }, 2500);

    return () => clearInterval(timer);
  }, [isAutoPlaying, isHovered, lightboxImage]);

  const triggerSlide = (targetIndexOrFn: number | ((prev: number) => number)) => {
    setIsSliding(true);
    setTimeout(() => {
      if (typeof targetIndexOrFn === 'function') {
        setCurrentIndex(targetIndexOrFn);
      } else {
        setCurrentIndex(targetIndexOrFn);
      }
      setTimeout(() => {
        setIsSliding(false);
      }, 200);
    }, 150);
  };

  const nextSlide = () => {
    triggerSlide((prev) => (prev + 1) % photoGallery.length);
  };

  const prevSlide = () => {
    triggerSlide((prev) => (prev - 1 + photoGallery.length) % photoGallery.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 40) {
      nextSlide();
    }
    if (touchStartX.current - touchEndX.current < -40) {
      prevSlide();
    }
  };

  // 5 Visible offsets centered around middle (0): [-2, -1, 0, 1, 2]
  const visibleOffsets = [-2, -1, 0, 1, 2];

  return (
    <div style={{ marginTop: '4rem', position: 'relative' }}>
      {/* Header Bar with Control Toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              background: 'rgba(0, 242, 254, 0.12)',
              border: '1px solid rgba(0, 242, 254, 0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--accent-cyan)'
            }}
          >
            <Camera size={20} />
          </div>
          <div>
            <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-title)', margin: 0 }}>
              Life, Trails & Discipline Showcase
            </h3>
          </div>
        </div>

        {/* Carousel Action Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-main)',
              borderRadius: 'var(--radius-full)',
              padding: '0.4rem 0.95rem',
              fontSize: '0.8rem',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              cursor: 'pointer',
              transition: 'var(--transition-fast)'
            }}
            title={isAutoPlaying ? 'Pause Autoplay' : 'Resume Autoplay'}
          >
            {isAutoPlaying ? <Pause size={14} color="var(--accent-cyan)" /> : <Play size={14} color="var(--accent-cyan)" />}
            <span>{isAutoPlaying ? (isHovered ? 'Paused (Hovered)' : 'Autoplay (2.5s)') : 'Paused'}</span>
          </button>

          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              onClick={prevSlide}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-title)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'var(--transition-fast)'
              }}
              aria-label="Previous Photo"
              title="Previous Photo"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={nextSlide}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-title)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'var(--transition-fast)'
              }}
              aria-label="Next Photo"
              title="Next Photo"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* 5-Card Middle Highlight Carousel Container */}
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          position: 'relative',
          minHeight: '440px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          padding: '1rem 0'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1.2rem',
            width: '100%',
            filter: isSliding ? 'blur(3px) opacity(0.88)' : 'blur(0px) opacity(1)',
            transition: 'filter 0.4s ease'
          }}
        >
          {visibleOffsets.map((offset) => {
            const photoIndex = (currentIndex + offset + photoGallery.length) % photoGallery.length;
            const photo = photoGallery[photoIndex];
            const isMiddle = offset === 0;
            const isInner = Math.abs(offset) === 1;

            return (
              <div
                key={`${photoIndex}-${offset}`}
                onClick={() => {
                  if (isMiddle) {
                    setLightboxImage(photo);
                  } else {
                    triggerSlide((currentIndex + offset + photoGallery.length) % photoGallery.length);
                  }
                }}
                style={{
                  flexShrink: 0,
                  width: isMiddle ? '320px' : isInner ? '250px' : '190px',
                  height: isMiddle ? '410px' : isInner ? '340px' : '280px',
                  borderRadius: 'var(--radius-lg)',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: isMiddle ? '2px solid var(--accent-cyan)' : '1px solid var(--border-color)',
                  boxShadow: isMiddle
                    ? '0 16px 40px rgba(0, 180, 216, 0.45)'
                    : isInner
                    ? '0 8px 24px rgba(0, 0, 0, 0.25)'
                    : 'none',
                  transform: isMiddle ? 'scale(1.08) translateY(-6px)' : isInner ? 'scale(0.95)' : 'scale(0.85)',
                  opacity: isMiddle ? 1 : isInner ? 0.72 : 0.4,
                  zIndex: isMiddle ? 10 : isInner ? 5 : 2,
                  transition: 'all 0.65s cubic-bezier(0.25, 1, 0.5, 1)',
                  background: 'var(--bg-card)'
                }}
                className="carousel-5card-item"
              >
                <img
                  src={photo.url}
                  alt={photo.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.65s ease'
                  }}
                />

                {/* Glassmorphism Title Overlay */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: isMiddle
                      ? 'linear-gradient(to top, rgba(4, 7, 16, 0.92) 0%, rgba(4, 7, 16, 0.35) 55%, transparent 100%)'
                      : 'linear-gradient(to top, rgba(4, 7, 16, 0.8) 0%, transparent 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    padding: isMiddle ? '1.4rem' : '0.9rem',
                    color: '#ffffff',
                    transition: 'all 0.4s ease'
                  }}
                >
                  <span
                    style={{
                      fontSize: isMiddle ? '0.75rem' : '0.68rem',
                      color: 'var(--accent-cyan)',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      marginBottom: '0.2rem'
                    }}
                  >
                    <Sparkles size={isMiddle ? 13 : 11} />
                    {photo.category}
                  </span>
                  <h4
                    style={{
                      fontSize: isMiddle ? '1.15rem' : '0.92rem',
                      fontWeight: 800,
                      margin: 0,
                      textShadow: '0 2px 4px rgba(0,0,0,0.6)',
                      lineHeight: 1.3
                    }}
                  >
                    {photo.title}
                  </h4>
                  {isMiddle && (
                    <div
                      style={{
                        marginTop: '0.6rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        fontSize: '0.78rem',
                        color: 'rgba(255,255,255,0.85)',
                        fontWeight: 600
                      }}
                    >
                      <Maximize2 size={13} />
                      <span>Click to Highlight / Full View</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pagination Dot Indicators */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.5rem',
          marginTop: '1rem'
        }}
      >
        {photoGallery.map((_, idx) => (
          <button
            key={idx}
            onClick={() => triggerSlide(idx)}
            style={{
              width: idx === currentIndex ? '28px' : '9px',
              height: '9px',
              borderRadius: 'var(--radius-full)',
              background: idx === currentIndex ? 'var(--gradient-primary)' : 'var(--border-color)',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
            aria-label={`Go to photo ${idx + 1}`}
          />
        ))}
      </div>

      {/* Fullscreen Lightbox Modal */}
      {lightboxImage && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(2, 5, 12, 0.95)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            backdropFilter: 'blur(16px)'
          }}
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            style={{
              position: 'absolute',
              top: '2rem',
              right: '2rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#ffffff',
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <X size={24} />
          </button>

          <img
            src={lightboxImage.url}
            alt={lightboxImage.title}
            style={{
              maxWidth: '90vw',
              maxHeight: '80vh',
              objectFit: 'contain',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-lg)'
            }}
            onClick={(e) => e.stopPropagation()}
          />

          <div style={{ marginTop: '1.2rem', textAlign: 'center', color: '#ffffff' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--accent-cyan)', fontWeight: 700 }}>
              {lightboxImage.category}
            </span>
            <h3 style={{ fontSize: '1.4rem', fontWeight: 800, margin: '0.3rem 0 0 0' }}>
              {lightboxImage.title}
            </h3>
          </div>
        </div>
      )}
    </div>
  );
};
